/**
 * Header-Suche – Live-Vorschläge am Kadence-Suchfeld (.search-form .search-field), rein additiv
 * (Enter/Submit der Kadence-Suche bleibt unverändert). Diagnose-Synonyme zuerst, dann WP-Treffer (entdoppelt).
 * Nachfolger von js/pc-header-search.js; Texte aus src/i18n (search.*).
 * @module components/header-search/header-search
 */
import { getPageLocale } from '../../shared/services/config.js';
import { escapeHtml } from '../../shared/utils/escape.js';
import { t } from '../../shared/utils/i18n.js';
import { getSynonyms, loadSynonyms, searchBase, searchSite } from './header-search.service.js';

const MIN_CHARS = 2;
const DEBOUNCE_MS = 300;
const MAX_DIAGNOSIS_HITS = 3;

/**
 * Text vereinheitlichen (Umlaute, Groß/klein, Sonderzeichen), damit "Thripse", "thripse" und "thrips" gleich zählen.
 * @param {unknown} text Text
 * @returns {string} normalisierter Text
 */
export function normalize(text) {
  return String(text ?? '')
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Pfad-Schlüssel zum Entdoppeln von Synonym- und WP-Treffern.
 * @param {string} url URL
 * @returns {string} Pfad ohne Host, Anker, Query und Schluss-Slash
 */
function urlKey(url) {
  const path = String(url || '')
    .replace(/^https?:\/\/[^/]+/i, '')
    .split('#')[0]
    .split('?')[0];
  return path.replace(/\/+$/, '').toLowerCase();
}

/**
 * Rang eines Treffers: 0 exakt, 1 beginnt mit, 2 Wortanfang, 3 enthalten, null kein Treffer.
 * @param {unknown} field Feldwert
 * @param {string} query normalisierte Eingabe
 * @returns {number|null} Rang
 */
function matchRank(field, query) {
  const value = normalize(field);
  if (!value) return null;
  if (value === query) return 0;
  if (value.indexOf(query) === 0) return 1;
  if (` ${value}`.indexOf(` ${query}`) > -1) return 2;
  if (value.indexOf(query) > -1) return 3;
  return null;
}

/**
 * Passende Diagnosen zur Eingabe.
 * @param {string} query Eingabe
 * @param {Array<object>|null} synonyms Synonym-Liste
 * @param {boolean} english englische Seite
 * @returns {Array<{label: string, url: string, hint: string}>} höchstens 3 Treffer
 */
export function findDiagnoses(query, synonyms, english) {
  const q = normalize(query);
  if (q.length < MIN_CHARS || !synonyms || !synonyms.length) return [];
  const base = searchBase();
  const toolUrl = base + t('search.diagnosisTool');
  const hits = [];
  for (const d of synonyms) {
    let best = null;
    for (const field of [d.deutscher_name, d.englischer_name, d.slug]) {
      const rank = matchRank(field, q);
      if (rank !== null && (best === null || rank < best)) best = rank;
    }
    if (best === null) continue;
    // EN: nur eigens hinterlegte EN-Artikel, nie der deutsche Beitrag
    const articles = english ? d.passende_artikel_en : d.passende_artikel;
    let url = null;
    if (articles && articles.length && articles[0]) {
      const [slug, anchor] = String(articles[0]).split('#');
      url = `${base}${english ? '/en/' : '/'}${slug}/${anchor ? `#${anchor}` : ''}`;
    }
    const label = english ? d.englischer_name || d.deutscher_name : d.deutscher_name;
    hits.push({
      rank: best,
      label,
      url: url || toolUrl,
      hint: url ? '' : t('search.hint'),
      length: String(label || '').length,
    });
  }
  hits.sort((a, b) => (a.rank !== b.rank ? a.rank - b.rank : a.length - b.length));
  return hits.slice(0, MAX_DIAGNOSIS_HITS);
}

/**
 * Ergebnisliste rendern.
 * @param {HTMLElement} results Container
 * @param {string} query Eingabe
 * @param {Array<{title: string, url: string}>} items WP-Treffer
 * @returns {void}
 */
function render(results, query, items) {
  const english = getPageLocale().language === 'en';
  const diagnoses = findDiagnoses(query, getSynonyms(), english);
  const shown = {};
  let html = '';
  if (diagnoses.length) {
    html += `<div class="pc-header-search__group-label">${escapeHtml(t('search.group'))}</div><ul class="pc-header-search__list">`;
    for (const hit of diagnoses) {
      shown[urlKey(hit.url)] = true;
      html +=
        `<li class="pc-header-search__item"><a class="pc-header-search__link" href="${escapeHtml(hit.url)}">${escapeHtml(hit.label)}` +
        (hit.hint ? ` <span class="pc-header-search__hint">${escapeHtml(hit.hint)}</span>` : '') +
        '</a></li>';
    }
    html += '</ul>';
  }
  const wpHits = (items || []).filter((item) => !shown[urlKey(item.url)]);
  if (wpHits.length) {
    if (diagnoses.length)
      html += `<div class="pc-header-search__group-label">${escapeHtml(t('search.articles'))}</div>`;
    html += '<ul class="pc-header-search__list">';
    // title kommt bereits HTML-kodiert aus der WP-REST-API (wie bisher unverändert eingesetzt)
    for (const item of wpHits) {
      html += `<li class="pc-header-search__item"><a class="pc-header-search__link" href="${escapeHtml(item.url)}">${item.title}</a></li>`;
    }
    html += '</ul>';
  }
  if (!html) html = `<div class="pc-header-search__empty">${escapeHtml(t('search.empty'))}</div>`;
  results.innerHTML = html;
  results.classList.add('pc-visible');
}

/**
 * Ergebnisse ausblenden.
 * @param {HTMLElement} results Container
 * @returns {void}
 */
function hideResults(results) {
  results.classList.remove('pc-visible');
  results.innerHTML = '';
}

/**
 * Ein Suchfeld anbinden.
 * @param {HTMLInputElement} input Suchfeld
 * @returns {void}
 */
function setupInput(input) {
  input.setAttribute('autocomplete', 'off');
  const wrap = input.closest('.search-form');
  const results = document.createElement('div');
  results.className = 'pc-header-search__results';
  wrap.appendChild(results);
  let timer = null;

  const runSearch = (query) => {
    results.innerHTML = `<div class="pc-header-search__loading">${escapeHtml(t('search.loading'))}</div>`;
    results.classList.add('pc-visible');
    searchSite(query).then(
      (items) => {
        render(results, query, items);
        // Synonyme beim ersten Tippen evtl. noch nicht da: nachladen und einmal neu rendern
        if (!getSynonyms()) {
          loadSynonyms().then((list) => {
            if (list && input.value.trim() === query) render(results, query, items);
          });
        }
      },
      () => hideResults(results)
    );
  };

  input.addEventListener('focus', () => {
    loadSynonyms();
  });
  input.addEventListener('input', () => {
    const query = input.value.trim();
    if (timer) clearTimeout(timer);
    if (query.length < MIN_CHARS) {
      hideResults(results);
      return;
    }
    timer = setTimeout(() => runSearch(query), DEBOUNCE_MS);
  });
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') hideResults(results);
  });
  document.addEventListener('click', (event) => {
    if (!wrap.contains(event.target)) hideResults(results);
  });
}

/**
 * Alle Header-Suchfelder anbinden.
 * @returns {void}
 */
export function initHeaderSearch() {
  document.querySelectorAll('.search-form .search-field').forEach(setupInput);
}
